export interface ServiceCanadaAddress {
  id: string;
  name: string;
  address_line1: string;
  city: string;
  province_code: string;
  postal_code: string;
  country: string;
  provinces: string[]; // Provinces this office serves
  postalCodePrefixes?: string[]; // For Ontario offices with specific postal code prefixes
}

export const SERVICE_CANADA_ADDRESSES: ServiceCanadaAddress[] = [
  {
    id: 'nl',
    name: 'Service Canada - Newfoundland and Labrador',
    address_line1: 'PO Box 9430 Station A',
    city: 'St. John\'s',
    province_code: 'NL',
    postal_code: 'A1A 2Y5',
    country: 'CANADA',
    provinces: ['NL']
  },
  {
    id: 'pe',
    name: 'Service Canada - Prince Edward Island',
    address_line1: 'PO Box 8000 Station Central',
    city: 'Charlottetown',
    province_code: 'PE',
    postal_code: 'C1A 8K1',
    country: 'CANADA',
    provinces: ['PE']
  },
  {
    id: 'ns',
    name: 'Service Canada - Nova Scotia',
    address_line1: 'PO Box 1687 Station Central',
    city: 'Halifax',
    province_code: 'NS',
    postal_code: 'B3J 3J4',
    country: 'CANADA',
    provinces: ['NS']
  },
  {
    id: 'nb-qc',
    name: 'Service Canada - New Brunswick and Quebec',
    address_line1: 'PO Box 250',
    city: 'Fredericton',
    province_code: 'NB',
    postal_code: 'E3B 4Z6',
    country: 'CANADA',
    provinces: ['NB', 'QC']
  },
  {
    id: 'on-lmn',
    name: 'Service Canada - Ontario (L, M or N)',
    address_line1: 'PO Box 5100 Station D',
    city: 'Scarborough',
    province_code: 'ON',
    postal_code: 'M1R 5C8',
    country: 'CANADA',
    provinces: ['ON'],
    postalCodePrefixes: ['L', 'M', 'N']
  },
  {
    id: 'on-kp',
    name: 'Service Canada - Ontario (K or P)',
    address_line1: 'PO Box 2013 Station Main',
    city: 'Timmins',
    province_code: 'ON',
    postal_code: 'P4N 8C8',
    country: 'CANADA',
    provinces: ['ON'],
    postalCodePrefixes: ['K', 'P']
  },
  {
    id: 'prairie',
    name: 'Service Canada - Alberta/Manitoba/Saskatchewan/Territories',
    address_line1: 'PO Box 2710 Station Main',
    city: 'Edmonton',
    province_code: 'AB',
    postal_code: 'T5J 2G4',
    country: 'CANADA',
    provinces: ['AB', 'MB', 'SK', 'NT', 'NU']
  },
  {
    id: 'bc-yt',
    name: 'Service Canada - British Columbia and Yukon',
    address_line1: 'PO Box 1177 Station CSC',
    city: 'Victoria',
    province_code: 'BC',
    postal_code: 'V8W 2V2',
    country: 'CANADA',
    provinces: ['BC', 'YT']
  }
];

/**
 * Determine the default Service Canada office based on deceased's province and postal code
 * @param province - Province code (e.g., 'ON', 'QC', 'BC')
 * @param postalCode - Full postal code (e.g., 'M1R 5C8')
 * @returns Service Canada address object
 */
export function getDefaultServiceCanadaAddress(
  province: string | undefined,
  postalCode: string | undefined
): ServiceCanadaAddress {
  if (!province) {
    // Default to first office if no province
    return SERVICE_CANADA_ADDRESSES[0];
  }

  const provinceUpper = province.toUpperCase();

  // Special handling for Ontario based on postal code prefix
  if (provinceUpper === 'ON' && postalCode) {
    const postalCodeUpper = postalCode.toUpperCase();
    const firstLetter = postalCodeUpper.charAt(0);

    if (['L', 'M', 'N'].includes(firstLetter)) {
      return SERVICE_CANADA_ADDRESSES.find(addr => addr.id === 'on-lmn') || SERVICE_CANADA_ADDRESSES[0];
    } else if (['K', 'P'].includes(firstLetter)) {
      return SERVICE_CANADA_ADDRESSES.find(addr => addr.id === 'on-kp') || SERVICE_CANADA_ADDRESSES[0];
    }
  }

  // Find office that serves this province
  const office = SERVICE_CANADA_ADDRESSES.find(addr => 
    addr.provinces.includes(provinceUpper)
  );

  return office || SERVICE_CANADA_ADDRESSES[0];
}

